import { useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, useTheme } from '@mui/material';
import { QcsTextField } from '../basic/QcsTextField';
import { QcsSelect } from '../basic/QcsSelect';
import { QcsFormControl } from '../basic/QcsFormControl';
import { QcsFormHelperText } from '../basic/QcsFormHelperText';
import { get } from 'lodash';
import { TimeInputOutput } from '@api/models/settings/TimeInputOutput';
import { toTimeInputOutputUnit } from '@api/models/enums/TimeInputOutputUnit';
import { PASSWORD_MANAGER_IGNORE_PROPS } from '../../../utils/constants';

export type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years';

interface Props {
  className?: string;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  outputUnit: TimeUnit;
  outputFormat?: 'number' | 'object';
  forcedUnit?: TimeUnit;
  defaultUnit?: TimeUnit;
  maxValue?: number;
}

const TIME_UNITS: Record<TimeUnit, { toSeconds: number; translationKey: string }> = {
  seconds: { toSeconds: 1, translationKey: 'timeInput.seconds' },
  minutes: { toSeconds: 60, translationKey: 'timeInput.minutes' },
  hours: { toSeconds: 3600, translationKey: 'timeInput.hours' },
  days: { toSeconds: 86400, translationKey: 'timeInput.days' },
  months: { toSeconds: 2629746, translationKey: 'timeInput.months' },
  years: { toSeconds: 31556952, translationKey: 'timeInput.years' },
};

export const TimeInput: FC<Props> = ({
  className,
  name,
  label,
  disabled,
  required,
  autoFocus,
  outputUnit = 'minutes',
  outputFormat = 'number',
  forcedUnit,
  defaultUnit = 'minutes',
  maxValue = 999999,
}) => {
  const { t } = useTranslation();
  const { errors, touched, submitCount, setFieldValue, values } = useFormikContext<any>();
  const theme = useTheme();
  const wasTouched = !!submitCount || !!get(touched, name);
  const errorText = get(errors, name) as string;
  const hasError = !!errorText;

  const convertBetweenUnits = (value: number, fromUnit: TimeUnit, toUnit: TimeUnit): number => {
    const valueInSeconds = value * TIME_UNITS[fromUnit].toSeconds;
    return valueInSeconds / TIME_UNITS[toUnit].toSeconds;
  };

  // Initialize displayValue by converting from output unit to display unit
  const getInitialDisplayValue = () => {
    const formValue = get(values, name);
    if (formValue == null) return '';

    // Handle object format input - use value directly
    if (typeof formValue === 'object') {
      return formValue.value;
    }

    // Handle number format input (backward compatibility) - convert from outputUnit
    const currentUnit = forcedUnit || defaultUnit;
    return convertBetweenUnits(Number(formValue), outputUnit, currentUnit);
  };

  // Initialize unit from object format or use default
  const getInitialUnit = (): TimeUnit => {
    const formValue = get(values, name);
    if (forcedUnit) return forcedUnit;
    if (typeof formValue === 'object' && formValue.unit) {
      return formValue.unit.toLowerCase() as TimeUnit;
    }
    return defaultUnit;
  };

  const [displayValue, setDisplayValue] = useState<string | number>(getInitialDisplayValue());
  const [unit, setUnit] = useState<TimeUnit>(getInitialUnit());
  const displayableUnits = Object.fromEntries(
    Object.entries(TIME_UNITS).filter(([unitKey]) => TIME_UNITS[unitKey as TimeUnit].toSeconds >= TIME_UNITS[outputUnit].toSeconds)
  );

  const setValue = (value: number) => {
    setDisplayValue(value);

    if ('object' === outputFormat) {
      // Output as {value, unit} object - no conversion, use selected unit directly
      const outputValue: TimeInputOutput = {
        value: value,
        unit: toTimeInputOutputUnit(unit),
      };
      setFieldValue(name, outputValue);
    } else {
      // Output as number (backward compatibility) - convert to outputUnit
      const valueInOutputUnit = convertBetweenUnits(value, unit, outputUnit);
      setFieldValue(name, valueInOutputUnit);
    }
  };

  useEffect(() => {
    setUnit(forcedUnit ?? defaultUnit);
  }, [forcedUnit, defaultUnit]);

  // Update displayValue when form value changes externally
  useEffect(() => {
    const formValue = get(values, name);
    if (formValue == null) {
      setDisplayValue('');
    } else {
      // Handle object format - use value directly and update unit if needed
      if (typeof formValue === 'object') {
        const storedUnit = formValue.unit.toLowerCase() as TimeUnit;

        // If the stored unit differs from current display unit, update the display unit
        if (storedUnit !== unit && !forcedUnit) {
          setUnit(storedUnit);
        }

        // Use value directly without conversion
        setDisplayValue(formValue.value);
      } else {
        // Handle number format (backward compatibility) - convert from outputUnit to display unit
        setDisplayValue(convertBetweenUnits(Number(formValue), outputUnit, unit));
      }
    }
  }, [values, name, outputUnit, unit, forcedUnit]);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (!inputValue) {
      setDisplayValue('');
      setFieldValue(name, null);
      return;
    }

    setValue(Number(inputValue));
  };

  const handleValueBlur = () => {
    if (!displayValue) return;

    const newValue = Number(displayValue);
    if (newValue <= maxValue) return;

    // if value exceeds maxValue, reset to maxValue
    setValue(maxValue);
  };

  const handleUnitChange = (event: any) => {
    const newUnit = event.target.value as TimeUnit;
    setUnit(newUnit);

    if (!displayValue) return;

    // Keep the display value unchanged, just update the unit
    if ('object' === outputFormat) {
      // Output as {value, unit} object - no conversion, just update the unit
      const outputValue: TimeInputOutput = {
        value: Number(displayValue),
        unit: toTimeInputOutputUnit(newUnit),
      };
      setFieldValue(name, outputValue);
    } else {
      // Output as number (backward compatibility) - convert to outputUnit with new unit
      const valueInOutputUnit = convertBetweenUnits(Number(displayValue), newUnit, outputUnit);
      setFieldValue(name, valueInOutputUnit);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    (e.target as any).blur();
  };

  const isUnitSelectionDisabled = disabled || !!forcedUnit;

  return (
    <QcsFormControl className={className} fullWidth>
      <Box
        sx={{
          display: 'flex',
          backgroundColor: theme.palette.common.white,
          height: '48px',
          borderRadius: '4px',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <QcsTextField
          label={label}
          fullWidth
          id={`${name}-value`}
          type="number"
          value={displayValue}
          required={required}
          onBlur={handleValueBlur}
          onChange={handleValueChange}
          onWheel={handleWheel}
          htmlInputProps={{ min: 0, max: maxValue, step: 1, placeholder: '0', ...PASSWORD_MANAGER_IGNORE_PROPS }}
          disabled={disabled}
          sx={{
            borderRadius: '4px',
            height: '100%',
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              border: 'none',
              '& input': { padding: '14px 16px', fontSize: '14px' },
              '&:hover fieldset': {
                border: '1px solid rgb(33 33 33)',
              },
              '&:hover': {
                borderColor: 'rgb(33 33 33)',
              },
            },
          }}
          autoFocus={autoFocus}
        />

        <QcsSelect
          sx={{
            borderLeft: `0px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.common.white,
            minWidth: '120px',
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              backgroundColor: 'transparent',
            },
            '& .MuiSelect-select': {
              padding: '14px 16px',
              fontSize: '14px',
              paddingRight: '32px !important',
            },
            '& .MuiSelect-icon': {
              right: '8px',
            },
          }}
          value={unit}
          onChange={handleUnitChange}
          disabled={isUnitSelectionDisabled}
        >
          {Object.entries(displayableUnits).map(([unitKey, unitConfig]) => (
            <MenuItem key={unitKey} value={unitKey}>
              {t(unitConfig.translationKey, { count: displayValue === '' ? 0 : Math.floor(Number(displayValue)) })}
            </MenuItem>
          ))}
        </QcsSelect>
      </Box>

      {wasTouched && hasError && <QcsFormHelperText error>{errorText}</QcsFormHelperText>}
    </QcsFormControl>
  );
};
